/**
 * Local folder storage via the File System Access API (Chromium desktop browsers).
 *
 * Media lives in the project's folder and is referenced by its path relative to that folder, which
 * keeps projects portable: move or copy the folder, open it again, and every clip finds its file.
 * Files imported from outside the folder are copied into it, so a project folder is always
 * self-contained. The folder handle is shared with the project store (`projects/folders.ts`).
 */
import type { MediaLocation } from '@ve/core';
import type { MediaInput } from '@ve/engine';
import { projectFolders, type ProjectFolders } from '../../projects/folders.ts';
import {
  MediaAccessError,
  type ImportOptions,
  type ImportSource,
  type MediaStorageProvider,
  type ProviderAccess,
  type StoredFile,
} from '../types.ts';

export const LOCAL_FOLDER_PROVIDER_ID = 'local-folder';

/** Sub-folder for files copied in from elsewhere. */
const DEFAULT_IMPORT_FOLDER = 'Imported';

/** File types offered by the import picker (the analyzer has the final say on what is usable). */
const MEDIA_ACCEPT: Record<string, string[]> = {
  'video/*': ['.mp4', '.mov', '.m4v', '.webm', '.mkv'],
  'audio/*': ['.mp3', '.m4a', '.aac', '.wav', '.flac', '.ogg', '.opus'],
  'image/*': ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.avif', '.heic', '.heif'],
};

const MEDIA_EXTENSIONS = new Set(Object.values(MEDIA_ACCEPT).flat());

/** Media files inside a dropped folder (recursive, skipping hidden entries). */
/** Media files under `dir`, each with the directory names leading to it (starting with `dir`'s). */
async function collectMediaFiles(
  dir: FileSystemDirectoryHandle,
  path: string[] = [dir.name],
): Promise<Array<{ handle: FileSystemFileHandle; path: string[] }>> {
  if (path.length > 9) return [];
  const out: Array<{ handle: FileSystemFileHandle; path: string[] }> = [];
  for await (const entry of dir.values()) {
    if (entry.name.startsWith('.')) continue;
    if (entry.kind === 'directory') {
      out.push(...(await collectMediaFiles(entry as FileSystemDirectoryHandle, [...path, entry.name])));
    } else if (MEDIA_EXTENSIONS.has(entry.name.slice(entry.name.lastIndexOf('.')).toLowerCase())) {
      out.push({ handle: entry as FileSystemFileHandle, path });
    }
  }
  return out.sort((a, b) => a.handle.name.localeCompare(b.handle.name, undefined, { numeric: true }));
}

function isAbort(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError';
}

function splitName(name: string): [string, string] {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? [name.slice(0, dot), name.slice(dot)] : [name, ''];
}

/** Characters that are invalid in file names on common file systems. */
function sanitizeName(name: string): string {
  // eslint-disable-next-line no-control-regex -- control characters are exactly what we strip
  return name.replace(/[\\/:*?"<>|\u0000-\u001f]/g, '_').trim() || 'media';
}

export class LocalFolderProvider implements MediaStorageProvider {
  readonly id = LOCAL_FOLDER_PROVIDER_ID;
  readonly label = 'Local folder';
  readonly canImport = true;

  constructor(
    /** Which folder belongs to which project (shared with the project store). */
    private readonly folders: ProjectFolders = projectFolders,
    /** Directory picker; injectable for tests. */
    private readonly pickDirectory: (
      options: DirectoryPickerOptions,
    ) => Promise<FileSystemDirectoryHandle> = (o) => window.showDirectoryPicker(o),
  ) {}

  isSupported(): boolean {
    return (
      typeof window !== 'undefined' &&
      window.isSecureContext &&
      'showDirectoryPicker' in window &&
      typeof indexedDB !== 'undefined'
    );
  }

  async getAccess(projectId: string): Promise<ProviderAccess> {
    if (!this.isSupported()) return this.unsupported();
    const root = await this.folders.get(projectId);
    if (!root) return { state: 'not-configured' };
    const permission = await root.queryPermission({ mode: 'readwrite' });
    return permission === 'granted'
      ? { state: 'ready', label: root.name }
      : { state: 'needs-permission', label: root.name };
  }

  async requestAccess(projectId: string, options: { relocate?: boolean } = {}): Promise<ProviderAccess> {
    if (!this.isSupported()) return this.unsupported();
    const existing = await this.folders.get(projectId);
    if (existing && !options.relocate) {
      const permission = await existing.requestPermission({ mode: 'readwrite' });
      if (permission === 'granted') return { state: 'ready', label: existing.name };
      return { state: 'needs-permission', label: existing.name };
    }
    try {
      const root = await this.pickDirectory({
        id: 'cutroom-media',
        mode: 'readwrite',
        startIn: existing ?? 'videos',
      });
      await this.folders.set(projectId, root);
      return { state: 'ready', label: root.name };
    } catch (err) {
      if (isAbort(err)) return this.getAccess(projectId); // user cancelled the picker
      throw err;
    }
  }

  /** The connected folder, if access is granted (used e.g. as the file picker's start folder). */
  async rootFor(projectId: string): Promise<FileSystemDirectoryHandle | null> {
    const root = await this.folders.get(projectId);
    if (!root || (await root.queryPermission({ mode: 'readwrite' })) !== 'granted') return null;
    return root;
  }

  async importFile(
    projectId: string,
    source: ImportSource,
    options: ImportOptions = {},
  ): Promise<StoredFile> {
    const root = await this.requireRoot(projectId);
    if (source.kind === 'handle') {
      const file = await source.handle.getFile();
      // Already inside the media folder: reference it in place, no copy.
      const path = await root.resolve(source.handle);
      if (path) {
        options.onProgress?.(1);
        return { location: this.locationOf(path.join('/'), file), file, name: file.name };
      }
      return this.copyIn(root, DEFAULT_IMPORT_FOLDER, file.name, file, options);
    }
    return this.copyIn(root, source.folder ?? DEFAULT_IMPORT_FOLDER, source.name, source.blob, options);
  }

  async open(projectId: string, location: MediaLocation): Promise<MediaInput> {
    const root = await this.folders.get(projectId);
    if (!root) throw new MediaAccessError('No media folder is connected to this project', 'no-access');
    if ((await root.queryPermission({ mode: 'readwrite' })) !== 'granted') {
      throw new MediaAccessError(`Access to the folder “${root.name}” has not been granted yet`, 'no-access');
    }
    let file: File;
    try {
      const handle = await this.resolvePath(root, location.ref);
      file = await handle.getFile();
    } catch (err) {
      if (err instanceof DOMException && (err.name === 'NotFoundError' || err.name === 'TypeMismatchError')) {
        throw new MediaAccessError(`“${location.ref}” was not found in “${root.name}”`, 'missing');
      }
      throw err;
    }
    if (location.size !== undefined && file.size !== location.size) {
      throw new MediaAccessError(`“${location.ref}” has changed since it was imported`, 'changed');
    }
    return file;
  }

  async pickFiles(projectId: string): Promise<ImportSource[]> {
    try {
      const handles = await window.showOpenFilePicker({
        id: 'cutroom-import',
        multiple: true,
        startIn: (await this.rootFor(projectId)) ?? 'videos',
        types: [{ description: 'Video, audio and images', accept: MEDIA_ACCEPT }],
      });
      return handles.map((handle) => ({ kind: 'handle', handle }));
    } catch (err) {
      if (isAbort(err)) return [];
      throw err;
    }
  }

  sourcesFromDrop(dataTransfer: DataTransfer): Promise<ImportSource[]> {
    // Handles must be requested synchronously while the drop event is being dispatched.
    const pending = [...dataTransfer.items]
      .filter((item) => item.kind === 'file' && typeof item.getAsFileSystemHandle === 'function')
      .map((item) => item.getAsFileSystemHandle!());
    return Promise.all(pending).then(async (handles) => {
      const sources: ImportSource[] = [];
      for (const handle of handles) {
        if (handle?.kind === 'file') sources.push({ kind: 'handle', handle: handle as FileSystemFileHandle });
        else if (handle?.kind === 'directory') {
          for (const { handle: file, path } of await collectMediaFiles(handle as FileSystemDirectoryHandle))
            sources.push({ kind: 'handle', handle: file, libraryPath: path });
        }
      }
      return sources;
    });
  }

  // -------------------------------------------------------------------------------------------

  private unsupported(): ProviderAccess {
    return {
      state: 'unsupported',
      reason: 'Local folders need the File System Access API (Chrome, Edge, Opera or Arc on desktop).',
    };
  }

  private async requireRoot(projectId: string): Promise<FileSystemDirectoryHandle> {
    const root = await this.rootFor(projectId);
    if (!root) throw new MediaAccessError('Connect a media folder before importing', 'no-access');
    return root;
  }

  private locationOf(ref: string, file: File): MediaLocation {
    return { provider: this.id, ref, size: file.size, lastModified: file.lastModified };
  }

  private async resolvePath(root: FileSystemDirectoryHandle, ref: string): Promise<FileSystemFileHandle> {
    const parts = ref.split('/').filter(Boolean);
    const fileName = parts.pop();
    if (!fileName) throw new DOMException('Empty path', 'NotFoundError');
    let dir = root;
    for (const part of parts) dir = await dir.getDirectoryHandle(part);
    return dir.getFileHandle(fileName);
  }

  /** Copies bytes into `<root>/<folder>/<name>`, choosing a free name, streaming with progress. */
  private async copyIn(
    root: FileSystemDirectoryHandle,
    folder: string,
    name: string,
    data: Blob,
    options: ImportOptions,
  ): Promise<StoredFile> {
    const folderName = sanitizeName(folder);
    const dir = await root.getDirectoryHandle(folderName, { create: true });
    const fileName = await this.freeName(dir, sanitizeName(name));
    const handle = await dir.getFileHandle(fileName, { create: true });
    const writable = await handle.createWritable();
    let copied = 0;
    const total = data.size || 1;
    const progress = new TransformStream<Uint8Array, Uint8Array>({
      transform(chunk, controller) {
        copied += chunk.byteLength;
        options.onProgress?.(Math.min(1, copied / total));
        controller.enqueue(chunk);
      },
    });
    try {
      await data.stream().pipeThrough(progress).pipeTo(writable, { signal: options.signal });
    } catch (err) {
      await dir.removeEntry(fileName).catch(() => undefined);
      throw err;
    }
    const file = await handle.getFile();
    return { location: this.locationOf(`${folderName}/${fileName}`, file), file, name: fileName };
  }

  private async freeName(dir: FileSystemDirectoryHandle, name: string): Promise<string> {
    const [base, ext] = splitName(name);
    for (let i = 1; i < 10_000; i++) {
      const candidate = i === 1 ? name : `${base} (${i})${ext}`;
      try {
        await dir.getFileHandle(candidate);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'NotFoundError') return candidate;
        throw err;
      }
    }
    throw new Error(`Could not find a free file name for ${name}`);
  }
}
